import { Maximize2, Minimize2 } from 'lucide-react'
import { cn } from '~/lib/utils'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Markdown } from '~/components/ui/markdown'
import { checklistCriteriaSections } from '~/lib/checklist-criteria'
import type { ExploreChecklistItem } from './types'

export interface ExploreDetailPanelProps {
    item: ExploreChecklistItem | null
    className?: string
    expanded?: boolean
    onToggleExpanded?(): void
}

/** 선택된 체크리스트의 기준·관리·현장 상세와 필요 데이터를 보여준다. */
export function ExploreDetailPanel({
    item,
    className,
    expanded = false,
    onToggleExpanded,
}: ExploreDetailPanelProps) {
    const detailSections = item
        ? checklistCriteriaSections(item).map((s) => ({
              title: s.title,
              text: s.content,
          }))
        : []

    return (
        <section
            className={cn(
                'bg-card border-border flex min-h-0 flex-col rounded-sm border',
                className
            )}>
            <div className='border-border flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3'>
                <h2 className='text-foreground text-sm font-bold'>항목 상세</h2>
                {item && onToggleExpanded ? (
                    <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={onToggleExpanded}
                        className='h-8 rounded-sm px-2 text-xs'
                        aria-label={
                            expanded ? '상세 패널 복원' : '상세 패널 최대화'
                        }>
                        {expanded ? (
                            <Minimize2 className='size-3.5' />
                        ) : (
                            <Maximize2 className='size-3.5' />
                        )}
                        {expanded ? '복원' : '크게 보기'}
                    </Button>
                ) : null}
            </div>
            {item ? (
                <div className='min-h-0 flex-1 overflow-y-auto p-4 lg:p-5'>
                    <div className='grid gap-5'>
                        <div className='border-border flex items-start gap-3 border-b pb-4'>
                            <span className='text-primary text-xl font-extrabold'>
                                {item.itemNumber ?? item.id}
                            </span>
                            <div className='min-w-0 flex-1'>
                                <div className='flex flex-wrap items-start gap-2'>
                                    <h2 className='text-foreground min-w-0 flex-1 text-base leading-7 font-bold whitespace-pre-line'>
                                        {item.title}
                                    </h2>
                                    <Badge tone='info'>
                                        0~{item.maxScore}점
                                    </Badge>
                                </div>
                                <p className='text-muted-foreground mt-1 text-sm'>
                                    {item.part} · {item.classification}
                                </p>
                            </div>
                        </div>

                        <dl className='border-border bg-background grid gap-3 rounded-sm border p-3 text-sm md:grid-cols-2 xl:grid-cols-3'>
                            <DetailRow
                                label='적용대상'
                                value={item.industry ?? '전체 업종 공통'}
                            />
                            <DetailRow
                                label='규모'
                                value={scaleLabel(item.scale)}
                            />
                            <DetailRow
                                label='대분류'
                                value={item.majorCategory ?? '-'}
                            />
                            <DetailRow
                                label='중분류'
                                value={item.middleCategory ?? '-'}
                            />
                            <DetailRow
                                label='데이터 유형'
                                value={item.dataType ?? '-'}
                            />
                        </dl>

                        <div className='grid gap-3 lg:grid-cols-3'>
                            {detailSections.map((section) => (
                                <TextSection
                                    key={section.title}
                                    title={section.title}
                                    text={section.text}
                                />
                            ))}
                        </div>

                        <div className='grid gap-2'>
                            <h3 className='text-foreground text-sm font-bold'>
                                필요 데이터
                            </h3>
                            <div className='flex flex-wrap gap-2'>
                                {item.fields.length > 0 ? (
                                    item.fields.map((field) => (
                                        <span
                                            key={field.fieldId}
                                            title={
                                                field.description ??
                                                field.question
                                            }
                                            className='border-border bg-background text-foreground rounded-sm border px-3 py-2 text-sm'>
                                            {field.nameKo}
                                            <span className='text-primary ml-1 font-bold'>
                                                · {field.dataType}
                                            </span>
                                        </span>
                                    ))
                                ) : (
                                    <p className='text-muted-foreground text-sm'>
                                        연결된 필요 데이터가 없습니다.
                                    </p>
                                )}
                            </div>
                        </div>

                        {item.scoringLogic ? (
                            <TextSection
                                title='평가 로직'
                                text={item.scoringLogic}
                            />
                        ) : null}
                    </div>
                </div>
            ) : (
                <div className='grid min-h-0 flex-1 place-items-center p-6 text-center'>
                    <p className='text-muted-foreground text-sm'>
                        위 체크리스트에서 항목을 선택하면 기준·관리·현장 상세와
                        필요 데이터가 표시됩니다.
                    </p>
                </div>
            )}
        </section>
    )
}

function DetailRow({ label, value }: { label: string; value: string }) {
    return (
        <div className='grid grid-cols-[5rem_minmax(0,1fr)] gap-3'>
            <dt className='text-muted-foreground'>{label}</dt>
            <dd className='text-foreground min-w-0 font-semibold'>{value}</dd>
        </div>
    )
}

function TextSection({ title, text }: { title: string; text: string }) {
    return (
        <article className='border-border bg-card rounded-sm border p-4'>
            <h3 className='text-navy text-sm font-extrabold'>{title}</h3>
            <Markdown className='mt-2 leading-6'>{text}</Markdown>
        </article>
    )
}

function scaleLabel(scale: string): string {
    // scale 값은 이미 한글(일반/소규모)이라 그대로 표시한다.
    return scale
}
